"use client";

type AttributionTouch = {
  source_platform: string;
  source_medium?: string;
  source_campaign?: string;
  source_content?: string;
  source_term?: string;
  source_click_id?: string;
  source_click_id_type?: string;
  source_referrer?: string;
  source_landing_page: string;
  source_captured_at: string;
};

type StoredAttribution = {
  first: AttributionTouch;
  last: AttributionTouch;
};

const STORAGE_KEY = "tutuly_source_attribution_v1";

const CLICK_ID_PLATFORMS: Record<string, string> = {
  fbclid: "meta",
  gbraid: "google_ads",
  gclid: "google_ads",
  li_fat_id: "linkedin",
  msclkid: "microsoft_ads",
  ttclid: "tiktok",
  wbraid: "google_ads",
};

function getStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function readStoredAttribution(): StoredAttribution | undefined {
  const storage = getStorage();
  if (!storage) return undefined;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredAttribution) : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredAttribution(attribution: StoredAttribution): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage may be unavailable in private browsing or strict browser modes.
  }
}

function inferPlatformFromReferrer(referrer: string): string | undefined {
  if (!referrer) return undefined;

  try {
    const hostname = new URL(referrer).hostname.toLowerCase();
    if (hostname.includes("google.")) return "google";
    if (hostname.includes("instagram.")) return "instagram";
    if (hostname.includes("facebook.") || hostname.includes("fb.")) return "meta";
    if (hostname.includes("tiktok.")) return "tiktok";
    if (hostname.includes("pinterest.")) return "pinterest";
    if (hostname.includes("youtube.") || hostname.includes("youtu.be")) return "youtube";
    if (hostname.includes("bing.")) return "bing";
    return hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function touchFromUrl(url: URL, referrer: string, now: Date): AttributionTouch | undefined {
  const params = url.searchParams;
  const clickIdType = Object.keys(CLICK_ID_PLATFORMS).find((key) => params.has(key));
  const referrerPlatform = inferPlatformFromReferrer(referrer);
  const sourcePlatform =
    params.get("utm_source") || (clickIdType ? CLICK_ID_PLATFORMS[clickIdType] : referrerPlatform);

  if (!sourcePlatform) return undefined;

  return {
    source_platform: sourcePlatform,
    source_medium: params.get("utm_medium") || (clickIdType ? "paid" : "referral"),
    source_campaign: params.get("utm_campaign") || undefined,
    source_content: params.get("utm_content") || undefined,
    source_term: params.get("utm_term") || undefined,
    source_click_id: clickIdType ? params.get(clickIdType) || undefined : undefined,
    source_click_id_type: clickIdType,
    source_referrer: referrer || undefined,
    source_landing_page: `${url.pathname}${url.search}`,
    source_captured_at: now.toISOString(),
  };
}

export function captureSourceAttribution(
  href = typeof window !== "undefined" ? window.location.href : "",
  referrer = typeof document !== "undefined" ? document.referrer : "",
  now = new Date(),
): StoredAttribution | undefined {
  if (!href) return readStoredAttribution();

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return readStoredAttribution();
  }

  const touch = touchFromUrl(url, referrer, now);
  if (!touch) return readStoredAttribution();

  const current = readStoredAttribution();
  const attribution = {
    first: current?.first ?? touch,
    last: touch,
  };

  writeStoredAttribution(attribution);
  return attribution;
}

export function getAttributionProperties(): Record<string, string> {
  const attribution = readStoredAttribution();
  if (!attribution) return {};

  const properties: Record<string, string> = {};
  const add = (key: string, value: string | undefined) => {
    if (value) properties[key] = value;
  };

  add("source_platform", attribution.last.source_platform);
  add("source_medium", attribution.last.source_medium);
  add("source_campaign", attribution.last.source_campaign);
  add("source_content", attribution.last.source_content);
  add("source_term", attribution.last.source_term);
  add("source_click_id", attribution.last.source_click_id);
  add("source_click_id_type", attribution.last.source_click_id_type);
  add("source_referrer", attribution.last.source_referrer);
  add("source_landing_page", attribution.last.source_landing_page);
  add("first_source_platform", attribution.first.source_platform);
  add("first_source_medium", attribution.first.source_medium);
  add("first_source_campaign", attribution.first.source_campaign);
  add("first_source_landing_page", attribution.first.source_landing_page);
  add("first_source_captured_at", attribution.first.source_captured_at);
  add("last_source_platform", attribution.last.source_platform);
  add("last_source_medium", attribution.last.source_medium);
  add("last_source_campaign", attribution.last.source_campaign);
  add("last_source_landing_page", attribution.last.source_landing_page);
  add("last_source_captured_at", attribution.last.source_captured_at);

  return properties;
}
